import {
  type CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";

import { useMutation, useQuery } from "@tanstack/react-query";
import moment from "moment";
import { Button, Header, Icon, Message, Table } from "semantic-ui-react";
import styled from "styled-components";

import { asMutation, initUseQuery } from "simplydo/core/query";
import SearchParams from "simplydo/core/searchParams";
import type { OpenAPI } from "simplydo/interfaces";

import api from "api";
import { useAppSelector } from "store";
import constants from "utils/constants";
import util from "utils/utils";

import { UserChip } from "components/lib/Chips";

import { PlaceholderItem, PlaceholderStack, TooltipButton } from "./UI";

type GetConnectionsRequest =
  OpenAPI.GET<"/connections/{entity}/{service}/{context_id}">;
type OnConnectCallback = (refreshCallback?: () => void) => void;
type OnRemoveConnectionResponse =
  OpenAPI.DELETE<"/connections/{entity}/{service}/{context_id}/{entity_id}">["response"];

export const useConnectedEntities = (
  entity: string,
  service: string,
  contextId: string,
  onConnect: OnConnectCallback = () => {},
  onDisconnect: (
    userId: string,
    response: OnRemoveConnectionResponse,
  ) => void = () => {},
  onConnectionError: (errorMessage: string) => void = () => {},
): {
  loading: boolean;
  errorMessage: string;
  connectedEntities: GetConnectionsRequest["response"]["entities"];
  removeConnection: (entityId: string) => void;
  isDisconnectingEntityId: string | undefined;
} => {
  const [errorMessage, setErrorMessage] = useState("");
  const { search } = useLocation();
  const navigate = useNavigate();

  const connectedEntityQuery = useQuery(
    initUseQuery(api.connections.getConnectedEntitiesForContext, [
      entity,
      service,
      contextId,
    ]),
  );

  const removeConnectionMutation = useMutation({
    mutationKey: ["connections", "remove", entity, service, contextId],
    mutationFn: asMutation(api.connections.removeConnection)!,
    onSuccess: (params, variables) => {
      onDisconnect(variables[3] as string, params);
      toast.success("Connection removed");
      api.invalidateQueries(api.connections.getConnectedEntitiesForContext, [
        entity,
        service,
        contextId,
      ]);
    },
  });

  const removeConnection = useCallback(
    (entityId: string) => {
      util
        .confirm(
          "Remove this connection?",
          "Are you sure you want to remove this connection? The selected user will immediately lose access to this resource.",
        )
        .then(() => {
          removeConnectionMutation.mutate([
            entity,
            service,
            contextId,
            entityId,
          ]);
        })
        .catch(() => {});
    },
    [entity, service, contextId, removeConnectionMutation],
  );

  useEffect(() => {
    const params = new SearchParams(search);
    const connectionSuccessString = params.get("connectionSuccess");
    const connectionService = params.get("connectionService");
    // We only show toasts / updates if this URL mutation is for our service
    if (connectionService !== service) return;
    if (connectionSuccessString) {
      if (connectionSuccessString === "false") {
        const nextErrorMessage =
          params.get("message") ||
          "Unable to connect to your service at this time.";
        setErrorMessage(nextErrorMessage);
        onConnectionError(nextErrorMessage);
      } else if (connectionSuccessString === "true") {
        onConnect(() => {
          connectedEntityQuery.refetch();
        });
      }

      params.delete("connectionSuccess");
      params.delete("connectionType");
      params.delete("connectionService");
      params.delete("message");
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [
    connectedEntityQuery,
    onConnect,
    onConnectionError,
    navigate,
    search,
    service,
  ]);

  return {
    loading: connectedEntityQuery.isLoading,
    errorMessage,
    connectedEntities: connectedEntityQuery.data?.entities || [],
    removeConnection,
    isDisconnectingEntityId: removeConnectionMutation.variables?.[3],
  };
};

const ConnectionsManagerContainer = styled.div`
  display: flex;
  gap: 10px;
  flex-direction: column;
  > p,
  h3,
  .ui.table {
    margin: 0 !important;
  }
`;

type ConnectionsManagerProps = {
  entity: string;
  entityId: string;
  serviceProvider: "microsoft";
  service: string;
  contextId: string;
  containerStyle?: CSSProperties;
  connectionButtonText: string;
  title?: string;
  subtitle?: string;
  establishConnection?: () => void;
  onConnect?: OnConnectCallback;
  onDisconnect?: (
    entityId: string,
    response: OnRemoveConnectionResponse,
  ) => void;
  readOnly?: boolean;
  warningMessage?: string;
  buttons?: React.ReactNode;
};

const ConnectionsManager = ({
  entity,
  entityId,
  service,
  serviceProvider,
  contextId,
  containerStyle = {},
  title,
  subtitle,
  connectionButtonText,
  onConnect,
  onDisconnect,
  readOnly = false,
  warningMessage,
  buttons,
}: ConnectionsManagerProps) => {
  const {
    loading,
    errorMessage,
    connectedEntities,
    removeConnection,
    isDisconnectingEntityId,
  } = useConnectedEntities(entity, service, contextId, onConnect, onDisconnect);
  const user = useAppSelector((state) => state.user);
  const [isConnecting, setIsConnecting] = useState(false);

  const currentEntityIsConnected = useMemo(
    () => connectedEntities?.some((entity) => entity._id === entityId),
    [connectedEntities, entityId],
  );

  return (
    <ConnectionsManagerContainer style={containerStyle}>
      {title && <Header as="h3">{title}</Header>}
      {subtitle && <p>{subtitle}</p>}
      {loading ?
        <PlaceholderStack gap={10}>
          <PlaceholderItem height={35} fluid />
          <PlaceholderItem height={15} fluid />
        </PlaceholderStack>
      : null}
      {!loading && warningMessage && !connectedEntities?.length ?
        <Message warning>
          <Message.Content>
            <Icon name="warning circle" /> {warningMessage}
          </Message.Content>
        </Message>
      : null}
      {!loading && errorMessage ?
        <Message error>
          <Message.Header>Could not get connections</Message.Header>
          <Message.Content>{errorMessage}</Message.Content>
        </Message>
      : null}
      {connectedEntities?.length ?
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>User</Table.HeaderCell>
              <Table.HeaderCell>Connected At</Table.HeaderCell>
              <Table.HeaderCell />
            </Table.Row>
          </Table.Header>
          {connectedEntities.map((connectedEntity) => (
            <Table.Row key={connectedEntity._id}>
              <Table.Cell>
                <UserChip user={connectedEntity.connection?.ownerUser} />
              </Table.Cell>
              <Table.Cell>
                {moment(connectedEntity?.connection?.generatedAt).format(
                  constants.challengeDeadlineMomentFormat,
                )}
              </Table.Cell>
              <Table.Cell
                style={{
                  display: "flex",
                  gap: "5px",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                }}
              >
                {(
                  connectedEntity.connection?.driveItemUrl &&
                  connectedEntity._id === user._id
                ) ?
                  <Button
                    secondary
                    as="a"
                    disabled={readOnly}
                    href={connectedEntity.connection.driveItemUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icon name="file excel outline" /> Open{" "}
                    {connectedEntity.connection?.driveItemName || "Excel file"}
                  </Button>
                : null}
                <TooltipButton
                  disabled={
                    readOnly ||
                    (entity === "user" && connectedEntity._id !== user._id)
                  }
                  icon="unlink"
                  basic
                  tooltip={
                    entity === "user" && connectedEntity._id !== user._id ?
                      "You cannot remove connections for other users"
                    : "Remove connection"
                  }
                  loading={isDisconnectingEntityId === connectedEntity._id}
                  onClick={() => removeConnection(connectedEntity._id!)}
                />
              </Table.Cell>
            </Table.Row>
          ))}
        </Table>
      : null}
      <div>
        {!loading && !currentEntityIsConnected ?
          <Button
            primary
            disabled={readOnly}
            content={connectionButtonText}
            loading={isConnecting}
            onClick={() => {
              setIsConnecting(true);
              util.connectOAuth(
                serviceProvider,
                user,
                entity,
                entityId,
                contextId,
                service,
              );
            }}
            style={{ maxWidth: "fit-content" }}
            icon="add"
          />
        : null}
        {buttons}
      </div>
    </ConnectionsManagerContainer>
  );
};

export default ConnectionsManager;
